"use client";

/**
 * What a product comes with, and the extras a shopper can add — the website
 * side of the till's add-ons (Petal_Some Ug., Oct 2026; rules in
 * lib/config/websiteAddOns.ts).
 *
 *   Comes with   the free pieces (always go): "3 × TEBLERONE".
 *   Add extras   the priced pieces: tick one, pick how many go with EACH item
 *                (the shop's count to start with).
 *
 * A piece linked to a product the shop also sells alone needs stock: a paid
 * one that has run out can't be ticked, a free one says it is not included.
 * Prices here are for showing; the bag and the order price every piece from
 * the product again.
 */

import { useEffect, useMemo } from "react";
import { useQuery } from "convex/react";
import { Gift, Minus, Plus } from "lucide-react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { isPaidAddOn, MAX_ADD_ON_QTY, websiteAddOns, type WebsiteAddOn } from "@/lib/config/websiteAddOns";

/** Piece id → how many with EACH item (paid pieces only; absent/0 = not taken). */
export type AddOnSelection = Record<string, number>;

/** What the ticked extras add to ONE item. */
export function selectionExtraPerItem(pieces: ReadonlyArray<WebsiteAddOn>, value: AddOnSelection): number {
  return pieces.reduce((s, p) => s + (isPaidAddOn(p) ? p.price * Math.max(0, value[p.id] || 0) : 0), 0);
}

/** The ticked extras as the bag wants them. */
export function selectionChoices(value: AddOnSelection): { id: string; qty: number }[] {
  return Object.entries(value)
    .filter(([, n]) => n > 0)
    .map(([id, qty]) => ({ id, qty }));
}

export function AddOnsPicker({
  pieces,
  value,
  onChange,
  itemQuantity = 1,
  showFree = true,
  showPaid = true,
  currency = "UGX",
}: {
  pieces: ReadonlyArray<WebsiteAddOn>;
  value: AddOnSelection;
  onChange: (next: AddOnSelection) => void;
  itemQuantity?: number;
  /** Off when the hire panel already lists the kit. */
  showFree?: boolean;
  /** Off when the price is on request (nothing is added to a cart). */
  showPaid?: boolean;
  currency?: string;
}) {
  const q = Math.max(1, Math.round(itemQuantity || 1));
  const free = showFree ? pieces.filter((p) => !isPaidAddOn(p)) : [];
  const paid = showPaid ? pieces.filter(isPaidAddOn) : [];

  // The shelf of every linked piece shown (convex/products/websiteTerms).
  const linkedIds = useMemo(
    () => Array.from(new Set([...free, ...paid].filter((p) => p.productId).map((p) => String(p.productId)))).sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify([...free, ...paid].map((p) => p.productId || ""))],
  );
  const stockRows = useQuery(
    api.products.websiteTerms.getAddOnStock,
    linkedIds.length ? { productIds: linkedIds as Id<"products">[] } : "skip",
  );
  /** Units on the shelf: a number, null = no limit, undefined = not linked / not known yet. */
  const shelf = (p: WebsiteAddOn): number | null | undefined =>
    p.productId ? stockRows?.find((r) => String(r.productId) === String(p.productId))?.available : undefined;
  /** The most of a paid piece that can go with each item. */
  const maxFor = (p: WebsiteAddOn): number => {
    const units = shelf(p);
    return typeof units === "number" ? Math.min(MAX_ADD_ON_QTY, Math.floor(units / q)) : MAX_ADD_ON_QTY;
  };

  // When the shelf turns out to hold fewer than ticked, take the count down.
  useEffect(() => {
    if (!stockRows) return;
    let changed = false;
    const next: AddOnSelection = {};
    for (const [id, n] of Object.entries(value)) {
      const piece = paid.find((p) => p.id === id);
      const capped = piece ? Math.min(n, maxFor(piece)) : n;
      if (capped !== n) changed = true;
      if (capped > 0) next[id] = capped;
    }
    if (changed) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockRows]);

  if (free.length === 0 && paid.length === 0) return null;

  const money = (n: number) => `${currency} ${Math.round(n).toLocaleString()}`;
  const set = (id: string, n: number) => {
    const next = { ...value };
    if (n > 0) next[id] = Math.min(MAX_ADD_ON_QTY, n);
    else delete next[id];
    onChange(next);
  };
  const extra = selectionExtraPerItem(paid, value);

  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-testid="add-ons-picker">
      {free.length > 0 && (
        <div className="space-y-1">
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-gray-900">
            <Gift className="h-3.5 w-3.5" aria-hidden />
            Comes with
          </p>
          <ul className="space-y-0.5 text-sm text-gray-700">
            {free.map((p) => {
              const units = shelf(p);
              const out = typeof units === "number" && units < p.qty * q;
              return (
                <li key={p.id} className={out ? "text-gray-400" : undefined}>
                  {p.qty * q} × {p.name}
                  {out ? <span className="ml-1 text-xs">(out of stock — not included)</span> : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {paid.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-900">Add extras</p>
          {paid.map((p) => {
            const max = maxFor(p);
            const out = max < 1;
            const count = Math.max(0, value[p.id] || 0);
            const on = count > 0 && !out;
            return (
              <div
                key={p.id}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
                  on ? "border-black bg-gray-50" : "border-gray-200 bg-white"
                } ${out ? "opacity-60" : ""}`}
              >
                <label className={`flex min-w-0 flex-1 items-center gap-2.5 ${out ? "cursor-not-allowed" : "cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={out}
                    onChange={(e) => set(p.id, e.target.checked ? Math.min(Math.max(1, p.qty), max) : 0)}
                    className="h-4 w-4 shrink-0 accent-black"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900">{p.name}</span>
                    <span className="block text-xs text-gray-500">{out ? "Out of stock" : `${money(p.price)} each`}</span>
                  </span>
                </label>
                {on && (
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => set(p.id, count - 1)}
                      aria-label={`One fewer ${p.name}`}
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 text-gray-800 hover:border-black"
                    >
                      <Minus className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <span className="w-6 text-center text-sm font-semibold tabular-nums text-gray-900" aria-live="polite">
                      {count}
                    </span>
                    <button
                      type="button"
                      onClick={() => set(p.id, count + 1)}
                      disabled={count >= max}
                      aria-label={`One more ${p.name}`}
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 text-gray-800 hover:border-black disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <p className="text-[11px] text-gray-500">
            {extra > 0 ? (
              <>
                Extras <span className="font-semibold text-gray-900">+ {money(extra * q)}</span>
                {q > 1 ? ` for ${q} items` : ""}.{" "}
              </>
            ) : null}
            Counts are for each item.
          </p>
        </div>
      )}
    </div>
  );
}

export default AddOnsPicker;
